// Copyright 2025 Sorama B.V.

import { autoinject, bindable, customElement } from "aurelia-framework";
import { Chart, ChartConfiguration } from "chart.js";
import { ReportableMeasurement } from "datatypes/ReportableMeasurement";
import { SorXMeasurement, SorXPDDData } from "datatypes/SorXMeasurement";
import { Point2 } from "libraries/DataTypes/Point2";

@autoinject
@customElement("sorx-pdd-plot-template")
export class SorXPDDPlotTemplate {
    @bindable
    public report: ReportableMeasurement<SorXMeasurement> | undefined;

    @bindable
    public pageNumber: number | undefined;

    @bindable
    public maxPages: number | undefined;

    public readonly chartContainer: HTMLElement | undefined;
    private dischargeChartCanvas: HTMLCanvasElement | undefined;
    private chartImage: HTMLImageElement | null = null;
    private prpdChartInstance: Chart | null = null;

    constructor() {}

    bind(): void {
        this.initializeChart();
    }

    private initializeChart(): void {
        if (!this.chartContainer) {
            console.warn("Chart container not found.");
            return;
        }

        // Create canvas
        this.dischargeChartCanvas = document.createElement("canvas");
        this.dischargeChartCanvas.style.minWidth = "800px";
        this.dischargeChartCanvas.width = 700;
        this.dischargeChartCanvas.height = 400;

        this.chartContainer.innerHTML = "";
        this.chartContainer.appendChild(this.dischargeChartCanvas);

        const ctx = this.dischargeChartCanvas.getContext("2d");
        if (!ctx) {
            console.error("Failed to get 2D context.");
            return;
        }
        const data = (this.report as ReportableMeasurement<SorXPDDData>).data;

        const chartDataPoints: Point2[] = data.dischargeDataPoints || [];

        const chartConfig: ChartConfiguration = {
            type: "scatter",
            data: {
                datasets: [
                    {
                        data: chartDataPoints,
                        backgroundColor: "rgba(0, 0, 0, 1)",
                        borderColor: "rgb(0, 0, 0, 1)",
                        borderWidth: 1,
                        pointRadius: 1,
                    },
                ],
            },
            options: {
                responsive: false,
                maintainAspectRatio: false,
                animation: { duration: 0 },
                layout: {
                    padding: {
                        left: 20,
                        right: 20,
                        top: 0,
                        bottom: 20,
                    },
                },
                scales: {
                    xAxes: [
                        {
                            type: "linear",
                            ticks: {
                                min: 0,
                                max: 360,
                                stepSize: 45,
                            },
                        },
                    ],
                    yAxes: [
                        {
                            ticks: { display: false },
                        },
                    ],
                },
                legend: {
                    display: false,
                },
                tooltips: {
                    enabled: false,
                },
            },
        };

        // Render chart
        this.prpdChartInstance = new Chart(ctx, chartConfig);

        this.prpdChartInstance.update();
        // Convert canvas to image
        const imageUrl = this.prpdChartInstance.toBase64Image();
        const img = new Image();
        img.src = imageUrl;
        img.style.width = "97%";
        img.style.height = "auto";

        // Replace canvas with image
        this.chartContainer.innerHTML = "";
        this.chartContainer.appendChild(img);

        // Cleanup
        this.destroy();
        this.chartImage = img;
    }

    private destroy(): void {
        if (this.prpdChartInstance) {
            this.prpdChartInstance.destroy();
            this.prpdChartInstance = null;
            this.dischargeChartCanvas = undefined;
        }
    }
}
